import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import { BsChatDotsFill } from 'react-icons/bs';
import { IoClose } from 'react-icons/io5';
import { useChat } from '../ChatProvider';
import ChatWindow from './ChatWindow';
import ChatPrivacyModal from './ChatPrivacyModal';

const TABS = {
  GROUP: 'group',
  TEACHER: 'teacher',
  TECH: 'tech',
};

const ChatPopup = () => {
  const {
    chatReady,
    ensureAuth,
    channelId,
    channelName,
    channelFname,
    hasTech,
    hasTeacher,
    techDmRoomId,
    teacherDmRoomId,
    teacherName,
    openTechDm,
    openTeacherDm,
    unreadChannel,
    unreadTeacher,
    unreadTech,
    privacyAccepted,
    acceptPrivacy,
  } = useChat();

  const [open, setOpen] = useState(false);
  const hasChannel = !!channelId;
  // Theo dõi unread của lần render trước để chỉ auto-open khi có tin nhắn MỚI
  // (tránh auto-open lại sau khi user đóng popup mà vẫn còn unread).
  const prevUnreadRef = useRef({ tech: 0, teacher: 0, channel: 0 });

  // Default tab: first available
  const getDefaultTab = () => {
    if (hasChannel) return TABS.GROUP;
    if (hasTeacher) return TABS.TEACHER;
    if (hasTech) return TABS.TECH;
    return TABS.GROUP;
  };
  const [activeTab, setActiveTab] = useState(getDefaultTab);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [teacherLoading, setTeacherLoading] = useState(false);
  const [teacherTried, setTeacherTried] = useState(false);
  const [techLoading, setTechLoading] = useState(false);
  const [techTried, setTechTried] = useState(false);

  const popupUnread = unreadChannel + unreadTeacher + unreadTech;

  const handleToggle = () => {
    if (!open && !chatReady) return;
    setOpen((prev) => !prev);
  };

  const handleTabClick = useCallback(
    async (tab) => {
      if (tab === TABS.TEACHER && !teacherDmRoomId && !teacherLoading && !teacherTried) {
        setTeacherLoading(true);
        await ensureAuth();
        await openTeacherDm();
        setTeacherLoading(false);
        setTeacherTried(true);
      }
      if (tab === TABS.TECH && !techDmRoomId && !techLoading && !techTried) {
        setTechLoading(true);
        await ensureAuth();
        await openTechDm();
        setTechLoading(false);
        setTechTried(true);
      }
      setActiveTab(tab);
    },
    [teacherDmRoomId, teacherLoading, teacherTried, techDmRoomId, techLoading, techTried, ensureAuth, openTeacherDm, openTechDm]
  );

  const handlePrivacyClose = () => {
    setShowPrivacy(false);
  };

  // Sau khi user bấm "Đồng ý" trong modal: chuyển sang tab Nhóm.
  // Không thể đọc `privacyAccepted` trong handlePrivacyClose vì context vừa
  // dispatch chưa flush, closure vẫn là false → cần callback explicit.
  const handlePrivacyAccept = () => {
    setActiveTab(TABS.GROUP);
  };

  // Auto-open popup khi có tin nhắn mới chưa đọc (chỉ trigger khi unread tăng,
  // không bật lại nếu user vừa đóng popup mà counter chưa giảm).
  useEffect(() => {
    if (!chatReady) return;
    const prev = prevUnreadRef.current;
    const teacherIncreased = unreadTeacher > prev.teacher;
    const techIncreased = unreadTech > prev.tech;
    const channelIncreased = unreadChannel > prev.channel;

    if (!open && (teacherIncreased || techIncreased || channelIncreased)) {
      // Ưu tiên DM (giáo viên / kỹ thuật) trước nhóm vì 1-1 thường khẩn hơn
      let nextTab = activeTab;
      if (teacherIncreased && hasTeacher) nextTab = TABS.TEACHER;
      else if (techIncreased && hasTech) nextTab = TABS.TECH;
      else if (channelIncreased && hasChannel) nextTab = TABS.GROUP;

      setActiveTab(nextTab);
      setOpen(true);
    }

    prevUnreadRef.current = {
      tech: unreadTech,
      teacher: unreadTeacher,
      channel: unreadChannel,
    };
  }, [unreadTech, unreadTeacher, unreadChannel, chatReady, open, activeTab, hasTeacher, hasTech, hasChannel, privacyAccepted]);

  // Auto-init DM on first tab open
  useEffect(() => {
    if (!open || !chatReady) return;
    if (activeTab === TABS.TEACHER && !teacherDmRoomId && !teacherLoading && !teacherTried) {
      setTeacherLoading(true);
      openTeacherDm().finally(() => {
        setTeacherLoading(false);
        setTeacherTried(true);
      });
    }
    if (activeTab === TABS.TECH && !techDmRoomId && !techLoading && !techTried) {
      setTechLoading(true);
      openTechDm().finally(() => {
        setTechLoading(false);
        setTechTried(true);
      });
    }
  }, [open, activeTab]);

  if (!chatReady) return null;
  if (!hasChannel && !hasTeacher && !hasTech) return null;

  const renderBody = () => {
    if (activeTab === TABS.GROUP) {
      return (
        <GroupBody>
          {!privacyAccepted && (
            <PrivacyNotice>
              <span>
                Bằng việc tiếp tục, bạn đồng ý với{' '}
                <PrivacyLink type='button' onClick={() => setShowPrivacy(true)}>
                  quy định nhóm
                </PrivacyLink>.
              </span>
              <ContinueBtn type='button' onClick={acceptPrivacy}>
                Tiếp tục
              </ContinueBtn>
            </PrivacyNotice>
          )}
          <ChatWindow
            roomId={channelId}
            height='100%'
            placeholder={`Nhắn tin tới ${channelFname || channelName || 'nhóm'}...`}
            disabled={!privacyAccepted}
            disabledPlaceholder='Bấm "Tiếp tục" ở trên để bắt đầu nhắn tin...'
          />
        </GroupBody>
      );
    }
    if (activeTab === TABS.TEACHER) {
      if (teacherLoading) return <EmptyMsg>Đang kết nối với giáo viên...</EmptyMsg>;
      if (teacherDmRoomId) {
        return (
          <ChatWindow
            roomId={teacherDmRoomId}
            type='dm'
            height='100%'
            placeholder={`Nhắn tin tới giáo viên...`}
          />
        );
      }
      return <EmptyMsg>Không thể kết nối với giáo viên.</EmptyMsg>;
    }
    if (activeTab === TABS.TECH) {
      if (techLoading) return <EmptyMsg>Đang kết nối với kỹ thuật viên...</EmptyMsg>;
      if (techDmRoomId) {
        return (
          <ChatWindow
            roomId={techDmRoomId}
            type='dm'
            height='100%'
            placeholder='Nhắn tin cho kỹ thuật viên...'
          />
        );
      }
      return <EmptyMsg>Không thể kết nối với kỹ thuật viên.</EmptyMsg>;
    }
    return null;
  };

  return (
    <>
      <ChatPrivacyModal
        show={showPrivacy}
        onHide={handlePrivacyClose}
        onAccept={handlePrivacyAccept}
      />

      {open && (
        <PopupWindow>
          <PopupHeader>
            <TabBar>
              {hasChannel && (
                <Tab $active={activeTab === TABS.GROUP} onClick={() => handleTabClick(TABS.GROUP)}>
                  Nhóm
                  {unreadChannel > 0 && <TabBadge>{unreadChannel}</TabBadge>}
                </Tab>
              )}
              {hasTeacher && (
                <Tab $active={activeTab === TABS.TEACHER} onClick={() => handleTabClick(TABS.TEACHER)}>
                  Giáo viên
                  {unreadTeacher > 0 && <TabBadge>{unreadTeacher}</TabBadge>}
                </Tab>
              )}
              {hasTech && (
                <Tab $active={activeTab === TABS.TECH} onClick={() => handleTabClick(TABS.TECH)}>
                  Kỹ thuật
                  {unreadTech > 0 && <TabBadge>{unreadTech}</TabBadge>}
                </Tab>
              )}
            </TabBar>
            <CloseBtn onClick={() => setOpen(false)}><IoClose size={18} /></CloseBtn>
          </PopupHeader>
          <PopupBody>{renderBody()}</PopupBody>
        </PopupWindow>
      )}

      <Fab onClick={handleToggle} title='Chat'>
        <BsChatDotsFill size={22} />
        {!open && popupUnread > 0 && <UnreadDot />}
      </Fab>
    </>
  );
};

export default ChatPopup;

const Fab = styled.button`
  position: relative;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 16px rgba(37, 99, 235, 0.5);
  }
`;

const UnreadDot = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ef4444;
  border: 2px solid #fff;
`;

const PopupWindow = styled.div`
  position: fixed;
  bottom: 120px;
  right: 10px;
  width: 370px;
  max-width: calc(100vw - 20px);
  height: 500px;
  max-height: calc(100vh - 180px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1100;
  border: 1px solid #e2e8f0;
`;

const PopupHeader = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 0 8px 0 0;
`;

const TabBar = styled.div`
  display: flex;
  flex: 1;
`;

const Tab = styled.button`
  flex: 1;
  padding: 12px 8px;
  border: none;
  background: none;
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  color: ${(p) => (p.$active ? '#2563eb' : '#64748b')};
  border-bottom: 2px solid ${(p) => (p.$active ? '#2563eb' : 'transparent')};
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: color 0.15s;

  &:hover {
    color: ${(p) => (p.$active ? '#2563eb' : '#334155')};
  }
`;

const TabBadge = styled.span`
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 8px;
  min-width: 16px;
  text-align: center;
  line-height: 14px;
`;

const CloseBtn = styled.button`
  border: none;
  background: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 6px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background 0.15s, color 0.15s;
  &:hover {
    color: #475569;
    background: #f1f5f9;
  }
`;

const PopupBody = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const EmptyMsg = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #64748b;
  font-size: 14px;
  padding: 20px;
  text-align: center;
  gap: 12px;
`;

const GroupBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const PrivacyNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  background: #fef3c7;
  border-bottom: 1px solid #fde68a;
  font-size: 12px;
  color: #78350f;
  line-height: 1.4;
`;

const PrivacyLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #b45309;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;

  &:hover {
    color: #92400e;
  }
`;

const ContinueBtn = styled.button`
  flex-shrink: 0;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #1d4ed8;
  }
`;
